// ./src/utils/appBrand.ts
//
// A2e #101 — the brand the DASHBOARD shows, decided from the white label of the host it was opened on (never a name
// written in code), and remembered per host so the next visit — and the installed app — starts with it.
//
//   • a first visit knows no brand: the loader, the tab and the header show the GENERIC icon until `whiteLabelByDomain`
//     answers (the app waits briefly for it — BrandingContext), then the brand;
//   • the answer is kept in localStorage under the HOST it was read for (`em.brand:<host>`), so the next visit starts
//     with it at once and the answer of that visit refreshes it; a host the API says has no white label is forgotten;
//   • the installed app (PWA) is described by a manifest built HERE, in the browser, from that brand — one build serves
//     every brand, so a manifest written at build time could only ever name one of them.
// ⚠ Pure (no DOM, no import.meta) so `check-pwa-brand` runs it. The DOM side is `components/generic/Favicon`, and
//   index.html's inline script reads the cached `display` before any bundle loads.
import { errorMessages } from '@em/api';

import type { DomainBranding } from './api/BrandingService';

/** The generic icon set (public/assets/app-icon) — no brand in it. */
export const GENERIC_ICON = {
  svg: '/assets/app-icon/icon.svg',
  png192: '/assets/app-icon/icon-192.png',
  png512: '/assets/app-icon/icon-512.png',
  appleTouch: '/assets/app-icon/apple-touch-icon.png',
  favicon32: '/assets/app-icon/favicon-32.png',
} as const;

/** localStorage key prefix — ⚠ index.html's inline script spells the same prefix (guarded). */
export const BRAND_CACHE_PREFIX = 'em.brand:';

export interface AppBrand {
  /** The white label's name; with none known, the host itself (true, and names no brand). */
  name: string;
  support: {
    email: string;
    /** The PLATFORM brand's website — its terms and privacy policy. '' when unknown (no legal links drawn). */
    website: string;
  };
  /** The header / loader mark: the white label's square icon, else (a host's own domain) its logo, else the generic. */
  logo: { mark: string };
  /** The white label's square icon (favicon, home-screen and installed-app icon), or null → the generic set. */
  iconUrl: string | null;
  /**
   * The brand the account belongs to — the payee on our receipts. Equal to `name` on a first-party domain; '' when it
   * cannot be known (a host's own domain on an older gateway, or no white label) — never the host's name.
   */
  platformName: string;
  /** The public event website's origin (the white label's `siteUrl`) — '' when unknown. */
  siteUrl: string;
  /** The browser chrome tint — the build's local STYLE, not brand identity. */
  themeColor: string;
  /** A host's own custom domain (not a first-party one). */
  isWhiteLabel: boolean;
}

const text = (value: unknown): string | null => {
  const v = typeof value === 'string' ? value.trim() : '';

  return v || null;
};

/** A local development host — the only kind where `?wlDomain=` may stand in for the real one. */
export const isLocalDevHost = (hostname: string): boolean => {
  const h = hostname.toLowerCase();

  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost');
};

/**
 * The host whose brand this page shows. ⚠ `?wlDomain=<domain>` (to try a custom domain locally) is honoured ON A LOCAL
 * HOST ONLY: on a live domain it would let anyone send a link that dresses our real sign-in page — and the installed app
 * — in another white label's name and logo (review 2026-10-05). ⚠ index.html's inline script applies the same rule
 * (`check-pwa-brand` runs both).
 */
export const brandHostOf = (hostname: string, search: string): string => {
  const override = isLocalDevHost(hostname) ? new URLSearchParams(search).get('wlDomain') : null;

  return (override || hostname || '').toLowerCase();
};

/** The brand for `host` from its white label (or none). Pure. */
export const appBrandFrom = (wl: DomainBranding | null | undefined, host: string, themeColor: string): AppBrand => {
  const name = text(wl?.brandName) ?? host;
  const iconUrl = text(wl?.faviconUrl);
  // A host's custom domain may have only a logo; a first-party one keeps to its square mark (its logo_url is the
  // small email logo).
  const hostLogo = wl?.isPlatform === false ? text(wl?.logoUrl) : null;

  return {
    name,
    support: {
      email: text(wl?.supportEmail) ?? '',
      // The platform's terms, never a host's site (they hold no terms of ours). An older gateway without
      // `platformWebsiteUrl` still knows a first-party domain's own website.
      website: text(wl?.platformWebsiteUrl) ?? (wl?.isPlatform ? text(wl?.websiteUrl) : null) ?? '',
    },
    logo: { mark: iconUrl ?? hostLogo ?? GENERIC_ICON.svg },
    iconUrl,
    platformName: text(wl?.platformName) ?? (wl?.isPlatform ? name : ''),
    siteUrl: text(wl?.siteUrl)?.replace(/\/+$/, '') ?? '',
    themeColor,
    isWhiteLabel: !!wl?.domain && wl?.isPlatform === false,
  };
};

/** What index.html's inline script reads before any bundle loads: the name and the mark (null = keep the generic). */
export interface CachedBrandDisplay {
  name: string;
  mark: string | null;
}

interface CachedBranding {
  v: 1;
  whiteLabel: DomainBranding;
  display: CachedBrandDisplay;
}

type ReadStorage = Pick<Storage, 'getItem'>;
type WriteStorage = Pick<Storage, 'setItem' | 'removeItem'>;

/** The white label remembered for `host`, or null (none, unreadable, or storage blocked). Never throws. */
export const readCachedBranding = (storage: ReadStorage | null, host: string): DomainBranding | null => {
  if (!storage || !host) {
    return null;
  }

  try {
    const parsed = JSON.parse(storage.getItem(`${BRAND_CACHE_PREFIX}${host}`) || 'null') as CachedBranding | null;
    const wl = parsed && parsed.v === 1 ? parsed.whiteLabel : null;

    return wl && typeof wl === 'object' && text(wl.brandName) ? wl : null;
  } catch {
    return null;
  }
};

/**
 * Remember the API's answer for `host` — or, when it answered that the host has NO white label, forget it. Only an
 * ANSWER is remembered: a failed read must not cost the next visit its brand. Never throws.
 */
export const writeCachedBranding = (storage: WriteStorage | null, host: string, wl: DomainBranding | null): void => {
  if (!storage || !host) {
    return;
  }

  try {
    const key = `${BRAND_CACHE_PREFIX}${host}`;
    if (!wl || !text(wl.brandName)) {
      storage.removeItem(key);

      return;
    }

    const brand = appBrandFrom(wl, host, '');
    const entry: CachedBranding = {
      v: 1,
      whiteLabel: wl,
      display: { name: brand.name, mark: brand.logo.mark === GENERIC_ICON.svg ? null : brand.logo.mark },
    };
    storage.setItem(key, JSON.stringify(entry));
  } catch {
    // Private mode / quota — the next visit simply starts without a brand.
  }
};

/** The white-label fields newer than some gateways (A2e #101) — asked for, and dropped one by one if refused. */
export const NEWER_BRAND_FIELDS = ['websiteUrl', 'logoUrl', 'isPlatform', 'platformName', 'platformWebsiteUrl', 'siteUrl'] as const;

/**
 * Which of `asked` a gateway refused BY NAME ("Cannot query field "x" on type "WhiteLabelConfig"") — [] for any other
 * failure (that is a failed read, not an older gateway). Pure; takes an OpResult.
 * ⚠ Reads EVERY error item (`errorMessages`): each GraphQL error arrives as its own nameless item, and the library's
 *   summary keeps only the LAST of them — one field per round trip (review 2026-10-05).
 */
export const refusedBrandFields = (result: any, asked: readonly string[]): string[] => {
  if (!result?.didFail?.()) {
    return [];
  }

  const message = errorMessages(result).join('\n');

  return asked.filter((f) => new RegExp(`Cannot query field ["']${f}["'] on type ["']WhiteLabelConfig["']`).test(message));
};

/**
 * Ask with every NEWER_BRAND_FIELD, then again without exactly the ones refused, until nothing is refused. Each pass
 * drops at least one field from a finite list, so it ends — at most one ask per field, plus the first.
 */
export const askDroppingRefused = async (ask: (fields: string[]) => Promise<any>): Promise<any> => {
  let fields: string[] = [...NEWER_BRAND_FIELDS];
  let result = await ask(fields);
  for (let refused = refusedBrandFields(result, fields); refused.length > 0; refused = refusedBrandFields(result, fields)) {
    fields = fields.filter((f) => !refused.includes(f));
    result = await ask(fields);
  }

  return result;
};

export interface ManifestIcon {
  src: string;
  sizes: string;
  type?: string;
  purpose?: string;
}

/** The type a browser is told for an icon URL (by its extension), or undefined when it cannot tell. */
export const iconTypeOf = (url: string): string | undefined => {
  const ext = (url.split(/[?#]/)[0].match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();

  return ({ svg: 'image/svg+xml', png: 'image/png', webp: 'image/webp', jpg: 'image/jpeg', jpeg: 'image/jpeg', ico: 'image/x-icon' } as Record<string, string>)[ext];
};

/**
 * The white label's icon as an installed-app icon — or null when it cannot be one (the generic set is used): an app
 * icon must be SQUARE and at least 144px (Chrome's install minimum). An SVG scales, so it is declared "any". Pure: the
 * caller measures the image.
 */
export const manifestIconOf = (url: string | null, width: number, height: number): ManifestIcon | null => {
  if (!url) {
    return null;
  }

  const type = iconTypeOf(url);
  if (width <= 0 || height <= 0 || width !== height) {
    return null;
  }

  if (type === 'image/svg+xml') {
    return { src: url, sizes: 'any', type };
  }

  if (width < 144) {
    return null;
  }

  return { src: url, sizes: `${width}x${height}`, ...(type ? { type } : {}) };
};

/** The generic icon set, at absolute URLs (a manifest held in a data: URL has no base to resolve against). */
export const genericManifestIcons = (origin: string): ManifestIcon[] => [
  { src: `${origin}${GENERIC_ICON.png192}`, sizes: '192x192', type: 'image/png' },
  { src: `${origin}${GENERIC_ICON.png512}`, sizes: '512x512', type: 'image/png' },
  { src: `${origin}${GENERIC_ICON.png512}`, sizes: '512x512', type: 'image/png', purpose: 'maskable' },
];

/**
 * The installed app's manifest for this brand on `origin`. ⚠ Every URL is ABSOLUTE (it is served from a data: URL), and
 * the `id` is the start page, so the app keeps one identity when the brand's name or icon changes.
 */
export const buildAppManifest = (brand: AppBrand, origin: string, icon: ManifestIcon | null) => ({
  id: '/',
  name: brand.name,
  short_name: brand.name,
  start_url: `${origin}/`,
  scope: `${origin}/`,
  display: 'standalone',
  theme_color: brand.themeColor,
  background_color: '#ffffff',
  icons: icon ? [icon] : genericManifestIcons(origin),
});

export interface HeadLink {
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
}

/**
 * The tab and home-screen icon links. ⚠ iOS ignores an SVG `apple-touch-icon`, so an SVG mark gives iOS the generic PNG.
 */
export const iconLinksOf = (brand: AppBrand): HeadLink[] => {
  const url = brand.iconUrl;
  if (!url) {
    return [
      { rel: 'icon', type: 'image/svg+xml', href: GENERIC_ICON.svg },
      { rel: 'icon', type: 'image/png', sizes: '32x32', href: GENERIC_ICON.favicon32 },
      { rel: 'apple-touch-icon', sizes: '180x180', href: GENERIC_ICON.appleTouch },
    ];
  }

  const type = iconTypeOf(url);

  return [
    { rel: 'icon', href: url, ...(type ? { type } : {}) },
    { rel: 'apple-touch-icon', href: type === 'image/svg+xml' ? GENERIC_ICON.appleTouch : url },
  ];
};
